import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ActionButton, ActionIconButton } from '../../components/ui/ActionButton';
import { ActionSymbol } from '../../components/ui/ActionSymbol';
import { NmIsoDateField } from '../../components/NmIsoDateField';
import { useAnnualPlanTranslation } from '../../hooks/useAnnualPlanTranslation';
import { strengthPlanApi, type StrengthPlanPreview, type StrengthPlanSettings, type StrengthPlanSession } from '../../services/strengthPlanApi';
import { displayDigits } from '../../utils/displayDigits';
import { todayISO, formatDateShort } from './helpers';
import { STRENGTH_OBJECTIVE_TEMPLATES } from './strengthObjectives';

export function StrengthPlanDialog({ atpId, scope, weekStart, phaseKey, onClose, onApplied }: {
  atpId: number; scope: 'week' | 'phase'; weekStart?: string; phaseKey?: string;
  onClose: () => void; onApplied: () => void;
}) {
  const { t, i18n } = useAnnualPlanTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLDialogElement | null>(null);
  useEffect(()=>{ const node=dialogRef.current; setPortalContainer(node); node?.showModal(); return ()=>node?.close(); },[]);
  const s = (key: string, values: Record<string, unknown> = {}) => t('annualPlan.strengthPlan.'+key, values);
  const [settings, setSettings] = useState<StrengthPlanSettings>({
    scope, week_start: scope === 'week' ? weekStart : undefined, phase_key: scope === 'phase' ? phaseKey : undefined,
    goal: 'preparation', experience: 'new', equipment: 'bodyweight', days: [2,5], from_date: todayISO(),
  });
  const [preview, setPreview] = useState<StrengthPlanPreview | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState<number | null>(null);
  const change = (patch: Partial<StrengthPlanSettings>) => { setSettings(prev => ({...prev,...patch})); setPreview(null); setCreated(null); setError(''); };
  const days = [...settings.days].sort((a,b)=>a-b);
  const validDays = days.length > 0 && days.length <= 2 && (days.length < 2 || Math.min(days[1]-days[0],7-(days[1]-days[0])) >= 2);
  const goalLabel = (goal: string) => t('annualPlan.strengthGoals.templates.'+goal+'.title');
  const description = (session: StrengthPlanSession) => [
    goalLabel(session.goal), s('warmup'), s('loadRule'), s('eachSide'),
    ...session.exercises.map(exercise => s('exerciseLine', { exercise: s('exercises.'+exercise.key), sets: exercise.sets, low: exercise.reps_min, high: exercise.reps_max, rpe: exercise.rpe, rest: exercise.rest_seconds })),
    s('progression'), session.reason === 'regular' ? '' : s('reasons.'+session.reason),
  ].filter(Boolean).join('\n');
  async function generate() {
    if (!validDays) return;
    setPending(true); setError(''); setCreated(null);
    try { setPreview(await strengthPlanApi.preview(atpId, settings)); }
    catch { setPreview(null); setError(s('previewFailed')); }
    finally { setPending(false); }
  }
  async function apply() {
    if (!preview) return;
    setPending(true); setError('');
    try {
      const result = await strengthPlanApi.apply(atpId, settings, preview, preview.sessions.filter(row=>!row.existing_id).map(row=>({
        date: row.date, title: s('sessionTitle', { goal: goalLabel(row.goal) }), description: description(row),
      })));
      setPreview(result); setCreated(result.created_ids.length); onApplied();
    } catch (failure) {
      const status = (failure as {response?:{status?:number}}).response?.status;
      setPreview(null); setError(s(status === 409 ? 'stale' : 'applyFailed'));
    } finally { setPending(false); }
  }
  return createPortal(<dialog ref={dialogRef} className="atp-strength-dialog" aria-labelledby="strength-plan-title"
      dir={i18n.dir()} onCancel={event=>{event.preventDefault();if(!pending)onClose();}}>
      <header><h2 id="strength-plan-title">{s('title')}</h2><ActionIconButton autoFocus disabled={pending} label={s('close')} icon={<ActionSymbol name="close"/>} onClick={onClose}/></header>
      <p>{s(scope === 'week' ? 'weekScope' : 'phaseScope', {value: scope === 'week' ? displayDigits(formatDateShort(weekStart ?? '')) : t('annualPlan.phases.'+phaseKey)})}</p>
      <fieldset disabled={pending}>
        <label>{s('goal')}<select value={settings.goal} onChange={event=>change({goal:event.target.value as StrengthPlanSettings['goal']})}>
          {STRENGTH_OBJECTIVE_TEMPLATES.map(goal=><option key={goal} value={goal}>{goalLabel(goal)}</option>)}
        </select></label>
        <div className="atp-strength-settings">
          <label>{s('experience')}<select value={settings.experience} onChange={event=>change({experience:event.target.value as StrengthPlanSettings['experience']})}><option value="new">{s('new')}</option><option value="trained">{s('trained')}</option></select></label>
          <label>{s('equipment')}<select value={settings.equipment} onChange={event=>change({equipment:event.target.value as StrengthPlanSettings['equipment']})}><option value="bodyweight">{s('bodyweight')}</option><option value="gym">{s('gym')}</option></select></label>
        </div>
        <label>{s('fromDate')}<NmIsoDateField value={settings.from_date} min={todayISO()} onChange={value=>change({from_date:value})} ariaLabel={s('fromDate')} portalContainer={portalContainer}/></label>
        <p>{s('daysHint')}</p>
        <div className="atp-strength-days" role="group" aria-label={s('days')}>
          {[1,2,3,4,5,6,7].map(day=><ActionButton key={day} size="sm" aria-pressed={days.includes(day)}
            disabled={!days.includes(day)&&days.length===2} onClick={()=>change({days:days.includes(day)?days.filter(value=>value!==day):[...days,day]})}>{s('weekdays.'+day)}</ActionButton>)}
        </div>
        {!validDays&&<p role="alert">{s('spacing')}</p>}
        <p>{s('rules')}</p>
        <ActionButton variant="primary" disabled={!validDays||!settings.from_date} onClick={()=>void generate()}>{s(pending?'loading':'preview')}</ActionButton>
      </fieldset>
      {error&&<p role="alert">{error}</p>}
      {created!=null&&<p role="status">{s('created',{count:created})}</p>}
      {preview&&<div className="atp-strength-preview">
        {preview.sessions.length===0&&<p>{s('empty')}</p>}
        {preview.skipped.length>0&&<p>{s('skipped',{count:preview.skipped.length})}</p>}
        {preview.sessions.map(session=><article key={session.date}>
          <h3>{displayDigits(formatDateShort(session.date))} · {goalLabel(session.goal)}</h3>
          {session.existing_id&&<p>{s('existing')}</p>}
          {session.reason!=='regular'&&<p>{s('reasons.'+session.reason)}</p>}
          <p>{s('dose',{sets:session.sets,low:session.reps_min,high:session.reps_max,rpe:session.rpe,rest:session.rest_seconds})}</p>
          <ul>{session.exercises.map(exercise=><li key={exercise.key}>{s('exerciseLine',{exercise:s('exercises.'+exercise.key),sets:exercise.sets,low:exercise.reps_min,high:exercise.reps_max,rpe:exercise.rpe,rest:exercise.rest_seconds})}</li>)}</ul>
        </article>)}
        <footer><ActionButton variant="primary" disabled={pending||!preview.sessions.some(row=>!row.existing_id)} onClick={()=>void apply()}>{s('apply')}</ActionButton><ActionButton disabled={pending} onClick={onClose}>{s('close')}</ActionButton></footer>
      </div>}
    </dialog>,document.body);
}
